import { HttpClient, HttpContext } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  ICoCoachAntwoord,
  ICoCoachContext,
  ICoCoachVraagRequest,
  ICoCoachVraagStart,
  ICoCoachVraagStatus,
} from '../interfaces/co-coach.interface';
import { IVideo } from '../interfaces/video.interface';
import { NO_ERROR_TOKEN } from '../interceptors/error.interceptor';
import { CoCoachWedstrijd } from '../helpers/co-coach/context';
import { MAX_WACHT_MS, peil } from '../helpers/co-coach/peiling';
import { AuthService } from './auth.service';
import { VideoService } from './video.service';
import { TranslateService } from '@ngx-translate/core';
import { apiFoutTekst } from '../helpers/api-fout';

/** Eén vraag met wat er (tot nu toe) op terugkwam. */
export interface CoCoachBeurt {
  /** Lokaal volgnummer, voor `track` en om de beurt terug te vinden. */
  sleutel: number;
  vraag: string;
  /** Pas bekend als de server de vraag heeft aangenomen. */
  id?: string;
  status: 'bezig' | 'klaar' | 'fout' | 'te-lang';
  stappen: string[];
  antwoord?: ICoCoachAntwoord;
  /** Melding van de server bij `fout`. */
  fout?: string;
  /** Wanneer de vraag gesteld is (ms), voor de grens van twee minuten. */
  gestart: number;
  /** Na "Opslaan als afspeellijst": de nieuwe lijst. */
  opgeslagen?: { sid: string | null; naam: string };
}

/**
 * De co-coach (variant C): de knop in de kopregel, het paneel en het gesprek.
 *
 * Of hij er is, vraagt de webapp één keer per sessie (GET
 * /CoCoach/Beschikbaar), vanuit de ingelogde schil. Zegt de server nee, of
 * lukt de vraag niet, dan blijft alles zoals het was: geen knop, geen paneel.
 *
 * Het gesprek staat hier en niet in het paneel, zodat het blijft staan als
 * het paneel even weg is (een pagina zonder kopregel, een andere pagina op de
 * telefoon). Peilen gebeurt alleen zolang het paneel open is; gaat het weer
 * open, dan loopt een vraag die nog bezig was gewoon verder.
 */
@Injectable({ providedIn: 'root' })
export class CoCoachService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly videoService = inject(VideoService);
  private readonly translate = inject(TranslateService);

  private readonly basis = `${environment.apiUrl}/CoCoach`;

  /** Zegt de server dat de co-coach er is voor deze gebruiker? */
  public readonly beschikbaar = signal(false);
  public readonly open = signal(false);

  /**
   * Wat de wedstrijdlijst nu toont (MatchListComponent); null zolang er geen
   * lijst in beeld is. Daaruit komt de context "deze wedstrijden". De lijst
   * geeft zijn eigen signaal door, zodat "Load more" en zoeken vanzelf
   * meelopen.
   */
  private readonly wedstrijdBron = signal<Signal<readonly CoCoachWedstrijd[]> | null>(null);
  public readonly zichtbareWedstrijden = computed<readonly CoCoachWedstrijd[] | null>(() => this.wedstrijdBron()?.() ?? null);

  public readonly beurten = signal<CoCoachBeurt[]>([]);
  public readonly bezig = computed(() => this.beurten().some((b) => b.status === 'bezig'));

  /** Welke beurt in de speler staat (null = de laatste met clips), en waar in de wachtrij. */
  public readonly speelSleutel = signal<number | null>(null);
  public readonly clipIndex = signal(0);

  private gesprekId: string | null = null;
  private volgnummer = 0;
  private geladen = false;
  private readonly peilingen = new Map<number, Subscription>();
  private readonly videos = new Map<string, Observable<IVideo | null>>();

  /** Eén keer per sessie; na uitloggen of wisselen van omgeving herlaadt de app toch. */
  public laad(): void {
    if (this.geladen || !this.auth.token) {
      return;
    }
    this.geladen = true;
    this.http.get<{ beschikbaar: boolean }>(`${this.basis}/Beschikbaar`, { context: this.stil() }).pipe(
      map((antwoord) => antwoord?.beschikbaar === true),
      catchError(() => of(false)),
    ).subscribe((ja) => this.beschikbaar.set(ja));
  }

  public openen(): void {
    if (!this.beschikbaar()) {
      return;
    }
    this.open.set(true);
    this.hervat();
  }

  /** Dicht: ook niet meer peilen (wat nog bezig is, loopt verder bij openen). */
  public sluiten(): void {
    this.open.set(false);
    this.stopPeilen();
  }

  public wissel(): void {
    if (this.open()) {
      this.sluiten();
    } else {
      this.openen();
    }
  }

  public toonWedstrijden(bron: Signal<readonly CoCoachWedstrijd[]>): void {
    this.wedstrijdBron.set(bron);
  }

  /** Alleen als het nog dezelfde lijst is (de volgende pagina kan hem al gezet hebben). */
  public vergeetWedstrijden(bron: Signal<readonly CoCoachWedstrijd[]>): void {
    if (this.wedstrijdBron() === bron) {
      this.wedstrijdBron.set(null);
    }
  }

  /** Een vraag stellen; een vervolgvraag gaat met hetzelfde gesprek mee. */
  public stel(vraag: string, context: ICoCoachContext): void {
    const tekst = (vraag ?? '').trim();
    if (!tekst || this.bezig()) {
      return;
    }
    const beurt: CoCoachBeurt = { sleutel: ++this.volgnummer, vraag: tekst, status: 'bezig', stappen: [], gestart: Date.now() };
    this.beurten.update((lijst) => [...lijst, beurt]);

    const verzoek: ICoCoachVraagRequest = { vraag: tekst, context };
    if (this.gesprekId) {
      verzoek.gesprekId = this.gesprekId;
    }
    this.http.post<ICoCoachVraagStart>(`${this.basis}/Vraag`, verzoek, { context: this.stil() }).subscribe({
      next: (start) => {
        if (!start?.id) {
          this.werkBij(beurt.sleutel, { status: 'fout' });
          return;
        }
        this.gesprekId = start.gesprekId || this.gesprekId;
        this.werkBij(beurt.sleutel, { id: start.id });
        if (this.open()) {
          this.volg(beurt.sleutel);
        }
      },
      error: (e) => this.werkBij(beurt.sleutel, { status: 'fout', fout: apiFoutTekst(this.translate, e, '') || undefined }),
    });
  }

  /** Opnieuw beginnen: het gesprek en de speler leeg. */
  public nieuwGesprek(): void {
    this.stopPeilen();
    this.gesprekId = null;
    this.beurten.set([]);
    this.speelSleutel.set(null);
    this.clipIndex.set(0);
  }

  /** Een andere beurt in de speler, vooraan in de wachtrij. */
  public speel(sleutel: number, index = 0): void {
    this.speelSleutel.set(sleutel);
    this.clipIndex.set(index);
  }

  public markeerOpgeslagen(sleutel: number, opgeslagen: { sid: string | null; naam: string }): void {
    this.werkBij(sleutel, { opgeslagen });
  }

  /**
   * De video van een clip (voor het pad in de opslag en, bij opslaan, de
   * teams). Eén keer per video per sessie; lukt het niet, dan null.
   */
  public video(videoSid: string): Observable<IVideo | null> {
    if (!this.videos.has(videoSid)) {
      this.videos.set(videoSid, this.videoService.getVideoById(videoSid).pipe(
        catchError(() => of(null)),
        shareReplay(1),
      ));
    }
    return this.videos.get(videoSid) as Observable<IVideo | null>;
  }

  // ── Peilen ──

  private volg(sleutel: number): void {
    const beurt = this.beurten().find((b) => b.sleutel === sleutel);
    if (!beurt?.id || beurt.status !== 'bezig' || this.peilingen.has(sleutel)) {
      return;
    }
    const id = beurt.id;
    const abonnement = peil(
      () => this.http.get<ICoCoachVraagStatus>(`${this.basis}/Vraag/${encodeURIComponent(id)}`, { context: this.stil() }),
      { gestart: beurt.gestart, max: MAX_WACHT_MS },
    ).subscribe({
      next: ({ status, uitkomst }) => {
        const wijziging: Partial<CoCoachBeurt> = {};
        if (status?.stappen) {
          wijziging.stappen = status.stappen;
        }
        if (uitkomst === 'klaar') {
          // Klaar zonder antwoord is voor de gebruiker hetzelfde als een fout.
          wijziging.antwoord = normaalAntwoord(status?.antwoord);
          wijziging.status = wijziging.antwoord ? 'klaar' : 'fout';
        } else if (uitkomst === 'fout') {
          wijziging.status = 'fout';
          wijziging.fout = status?.fout || undefined;
        } else if (uitkomst === 'te-lang') {
          wijziging.status = 'te-lang';
        }
        this.werkBij(sleutel, wijziging);
        if (uitkomst === 'klaar' && wijziging.antwoord?.clips.length) {
          // Een nieuw antwoord met clips komt meteen in de speler.
          this.speel(sleutel, 0);
        }
      },
      complete: () => this.peilingen.delete(sleutel),
    });
    if (!abonnement.closed) {
      this.peilingen.set(sleutel, abonnement);
    }
  }

  private hervat(): void {
    for (const beurt of this.beurten()) {
      if (beurt.status === 'bezig' && beurt.id) {
        this.volg(beurt.sleutel);
      }
    }
  }

  private stopPeilen(): void {
    this.peilingen.forEach((abonnement) => abonnement.unsubscribe());
    this.peilingen.clear();
  }

  private werkBij(sleutel: number, wijziging: Partial<CoCoachBeurt>): void {
    this.beurten.update((lijst) => lijst.map((b) => (b.sleutel === sleutel ? { ...b, ...wijziging } : b)));
  }

  /** Geen rode melding van de interceptor: het paneel zegt zelf wat er misging. */
  private stil(): HttpContext {
    return new HttpContext().set(NO_ERROR_TOKEN, true);
  }
}

/** Lege lijsten in plaats van ontbrekende velden, zodat het sjabloon niet hoeft te twijfelen. */
function normaalAntwoord(antwoord: ICoCoachAntwoord | undefined): ICoCoachAntwoord | undefined {
  if (!antwoord) {
    return undefined;
  }
  return {
    ...antwoord,
    clips: (antwoord.clips ?? []).filter((c) => !!c?.videoSid),
    vervolgvragen: (antwoord.vervolgvragen ?? []).filter((v) => !!v?.trim()),
  };
}
